import { centsToDollars } from '@core/util/money';
import {
	ActingForType,
	Brokerage,
	BusinessMatterClient,
	MatterBusinessUser,
	MatterFileType,
	MatterPropertyDetails,
	MatterTitle,
	PersonMatterClient,
	RegistrationMethodType,
} from '@models/matter';
import { LawFirm } from '@models/lawFirms';
import { MatterMortgageWithAssociations } from '@models/mortgage';
import {
	FCTTitleInsuranceState,
	TitleInsuranceProvider,
} from '@models/title-insurance';
import { Selector } from '@ngxs/store';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { MatterMortgagesState } from '@state/matter/matter_mortgages';
import { isSet } from '@core/util/value';
import { MatterClients } from '../matter_clients';
import { BusinessUsersState } from '@state/business-users/business-users.state';
import { BusinessUser } from '@models/businesses';
import {
	getActingFor,
	getBorrowerSolicitor,
	getBrokerage,
	getMortgage,
	getOwners,
	getParcels,
	getPins,
	getTransactionType,
	getVendorSolicitor,
} from './fct.util';
import { getFCTPropertyType } from '@core/util/property';
import { convertCloserAddressToFCTAddress } from '@core/util/address';
import {
	FCTSendData,
	MatterFCTResidentialTitleInsurance,
} from '@models/title-insurance/fct';
import { cloneDeep, isEqual } from 'lodash';
import { removeUndefinedAndNull } from './stewart.util';
import { MatterTitlesState } from '@state/matter/titles/titles.state';

export class FCTSelectors {
	@Selector([
		SelectedMatter.matterNumber,
		SelectedMatter.matterFileType,
		BusinessUsersState.getUsers,
		MatterClients.get_mortgagorsWithAssociationMapped,
		MatterMortgagesState.getAllMortgagesWithAssociations,
		SelectedMatter.propertyDetails,
		SelectedMatter.purchasersResideAtProperty,
		SelectedMatter.getTitleInsuranceProvider,
		SelectedMatter.clientBrokerage,
		SelectedMatter.otherPartyLawFirm,
		SelectedMatter.assignedClerk,
		SelectedMatter.assignedLawyer,
		SelectedMatter.actingFor,
		SelectedMatter.getTitleInsuranceResolvedPurchasePrice,
		SelectedMatter.closingDate,
		SelectedMatter.titleInsuranceThirdPartyId,
		MatterTitlesState.getAll,
	])
	static getFCTDataPayload(
		matterNumber: string,
		fileType: MatterFileType,
		users: BusinessUser[],
		clients: (PersonMatterClient | BusinessMatterClient)[],
		mortgages: MatterMortgageWithAssociations[],
		propertyDetails: MatterPropertyDetails,
		purchasersResideAtProperty: boolean,
		provider: TitleInsuranceProvider,
		clientBrokerage: Brokerage,
		otherPartyLawFirm: LawFirm,
		clerkAssigned: MatterBusinessUser,
		lawyerAssigned: MatterBusinessUser,
		actingFor: ActingForType,
		purchasePrice: number,
		closingDate: string,
		thirdPartyId: string,
		matterTitles: MatterTitle[],
	): FCTSendData {
		if (provider !== TitleInsuranceProvider.FCT) {
			return null;
		}
		let assignedUser: BusinessUser;

		if (users) {
			assignedUser = users?.find((x) => x.id === clerkAssigned?.id);
			if (!assignedUser) {
				assignedUser = users?.find((x) => x.id === lawyerAssigned.id);
			}
		}
		const data: FCTSendData = {
			matterNumber,
			fileNumber: matterNumber,
			transactionType: getTransactionType(fileType, propertyDetails),
			contactFirstName: assignedUser?.firstName ?? '',
			contactLastName: assignedUser?.lastName ?? '',
			owners: getOwners(clients) ?? [],
			actingFor: getActingFor(fileType, actingFor, mortgages),
			property: {
				propertyType: getFCTPropertyType(propertyDetails?.propertyType),
				occupancy: purchasersResideAtProperty ? 'Owner' : 'Rental',
				registration:
					propertyDetails?.registrationMethod ===
					RegistrationMethodType.LAND_TITLES
						? 'LT'
						: 'R',
				parcels: getParcels(propertyDetails, matterTitles),
				pins: getPins(propertyDetails, matterTitles),
			},
			mortgages: mortgages
				.filter((x) => x.priority !== null)
				.map((m) => getMortgage(m))
				.filter((x) => x.lenderName && x.amount),
		};

		if (fileType === MatterFileType.PURCHASE) {
			data.purchasePrice = Number(centsToDollars(purchasePrice));
		}

		if (data.mortgages?.length === 1) {
			data.mortgages[0].primary = true;
		}

		if (isSet(propertyDetails?.propertyType?.numberOfUnits)) {
			data.property.numberOfUnits =
				propertyDetails?.propertyType?.numberOfUnits;
		}

		const mmsMortgage = mortgages?.find((x) => x.mms);
		if (mmsMortgage) {
			data.mmsDeal = true;
			data.mmsDealUrn = mmsMortgage.dealId?.replace(/ /, '');
		} else {
			data.mmsDeal = false;
		}

		const address = convertCloserAddressToFCTAddress(propertyDetails?.address);
		if (isSet(address)) {
			data.property.address = address;
		}

		if (closingDate) {
			data.closingDate = closingDate;
		}

		if (thirdPartyId) {
			data.dealUrn = thirdPartyId;
		}

		if (fileType === MatterFileType.PURCHASE && otherPartyLawFirm) {
			data.vendorSolicitor = getVendorSolicitor(otherPartyLawFirm);
		}

		if (
			fileType === MatterFileType.REFINANCE &&
			actingFor === ActingForType.MORTGAGEE
		) {
			data.borrowerSolicitor = getBorrowerSolicitor(otherPartyLawFirm);
		}

		if (clientBrokerage) {
			data.realEstateAgent = getBrokerage(clientBrokerage);
		}

		return data;
	}

	@Selector([FCTSelectors.getFCTDataPayload, SelectedMatter.getTitleInsurance])
	static getPayloadHasChanged(
		curr: FCTSendData,
		titleInsurance: MatterFCTResidentialTitleInsurance,
	): boolean {
		let payloadHasChanged = true;
		if (titleInsurance?.provider !== TitleInsuranceProvider.FCT) {
			return false;
		}

		//for the case of getting a quote first and not submitting
		if (
			!titleInsurance?.state ||
			titleInsurance?.state === FCTTitleInsuranceState.DRAFT
		) {
			return false;
		}

		if (!titleInsurance?.previousSubmittedPayload) {
			return false;
		}

		if (titleInsurance?.previousSubmittedPayload && !curr) {
			return true;
		}

		const previousPayload = cloneDeep(titleInsurance?.previousSubmittedPayload);
		const currentPayload = cloneDeep(curr);

		if (
			isEqual(
				removeUndefinedAndNull(previousPayload),
				removeUndefinedAndNull(currentPayload),
			)
		) {
			payloadHasChanged = false;
		}

		return payloadHasChanged;
	}
}
